import type { Action } from 'vuex'
import type { Params, State } from './state'
import type {
  QueryTransactionHistoryListModel,
  TransactionHistoryItem,
} from '@/api/transaction-history/model/transactionHistory'
import type { CodesDetailItemsModel } from '@/api/model/codesModel'

import actionTypes from './actionTypes'
import {
  queryTransactionList,
  getReceiveCount,
  SendCount,
  queryRemindMailSendList,
} from '@/api/send_mail/send_mail'
import { useStorage } from '@/hooks/core/useStorage'
import { formatDate } from '@/utils'
import usePageData, { Feature, PageCode } from '@/hooks/core/usePageData'
import { computed } from 'vue'
import { CodesEnum } from '@/enums/codesEnum'

import type { CodesDetail } from '@/api/model/basicTypes'

const { loadCodesInformation } = useStorage()
const { savePageData } = usePageData()

const initializationFactory = <T = any>(data: T): void =>
   savePageData<T>(PageCode.TRANSACTION_HISTORY, Feature.INITIALIZATION, data)

const actions: { [actionTypes: string]: Action<State, any> } = {
  [actionTypes.SET_SEND_MAIL_DETAIL]: async ({ commit, state }): Promise<void> => {
    // リストを取得
    const result: QueryTransactionHistoryListModel | undefined =
      await queryTransactionList(state.params)

    const data: TransactionHistoryItem[] = result?.detail ?? []

    const transactionStatusCodes = computed<CodesDetailItemsModel[]>(() => {
      const documentTypeInfo = loadCodesInformation()?.find(
        (item) => item.management_number === CodesEnum.TRANSACTION_STATUS
      )

      return documentTypeInfo?.item ?? []
    })

    const documentTypeCodes = computed<CodesDetailItemsModel[]>(() => {
      const documentTypeInfo = loadCodesInformation()?.find(
        (item) => item.management_number === CodesEnum.DOCUMENT_TYPE
      )

      return documentTypeInfo?.item ?? []
    })

    // データをフォーマット
    data.forEach((item: TransactionHistoryItem) => {
      item.transaction.receive_user_name = item.transaction_status.receive_user_name

      item.transaction.revision_date = formatDate(item.transaction.revision_date ?? '')
      item.transaction.sent_date = formatDate(item.transaction.sent_date ?? '')
      item.transaction.receive_date = formatDate(item.transaction.receive_date ?? '')

      item.transaction.transaction_status =
        item.transaction_status.transaction_status || item.transaction.transaction_status

      transactionStatusCodes.value.forEach((items: CodesDetailItemsModel) => {
        if (items.value == item.transaction.transaction_status) {
          item.transaction.transaction_status_name = items.name
        }
      })

      documentTypeCodes.value.forEach((items: CodesDetailItemsModel) => {
        if (items.value == item.transaction.document_type) {
          item.transaction.document_type_name = items.name
        }
      })
    })

    commit(actionTypes.SET_SEND_MAIL_DETAIL, data)
    commit(actionTypes.SET_TOTAL_COUNT, result?.total_count ?? 0)
  },

  [actionTypes.SET_PARAMS]: ({ commit }, params: Params): void => {
    commit(actionTypes.SET_PARAMS, params)
  },

  [actionTypes.SET_RECEIVE_COUNT]: async ({ commit }): Promise<void> => {
    const result = await getReceiveCount()
    let data = result.data.detail.count
    if (data > 99) {
      data = '99+'
    }

    savePageData(PageCode.SIDEBAR, Feature.INITIALIZATION, { receiveCount: data })

    commit(actionTypes.SET_RECEIVE_COUNT, data)
  },

  [actionTypes.SET_SEND_COUNT]: async ({ commit }): Promise<void> => {
    const result = await SendCount()
    let data = result.data.detail.count
    if (data > 99) {
      data = '99+'
    }
    savePageData(PageCode.SIDEBAR, Feature.INITIALIZATION, { sendCount: data })
    commit(actionTypes.SET_SEND_COUNT, data)
  },

  [actionTypes.SET_FOLDER_CHANGE_MODAL_STATUS]: ({ commit }, params: boolean): void => {
    commit(actionTypes.SET_FOLDER_CHANGE_MODAL_STATUS, params)
  },


  //取引ステータスセレクトボックス処理
  [actionTypes.SET_TRANSACTION_STATUS_INFO]({ commit }): void {
    const codes: CodesDetail[] | undefined = loadCodesInformation()

    if (!codes) {
      return
    }

    const info = codes.find(
      (item: CodesDetail) => item.management_number === CodesEnum.TRANSACTION_STATUS
    ) as CodesDetail

    commit(actionTypes.SET_TRANSACTION_STATUS_INFO, info)
  },

  [actionTypes.SET_TRANSACTION_STATUS]({ commit }, value: string) {
    initializationFactory({ transactionStatus: value })
    commit(actionTypes.SET_TRANSACTION_STATUS, value)
  },

  [actionTypes.SET_REMIND_SEND_MAIL_DETAIL]: async ({ commit, state }): Promise<void> => {
    // リストを取得
    const result: QueryTransactionHistoryListModel | undefined =
      await queryRemindMailSendList(state.params)
    
    const data: TransactionHistoryItem[] = result?.detail ?? []
    const transactionStatusCodes = computed<CodesDetailItemsModel[]>(() => {
      const documentTypeInfo = loadCodesInformation()?.find(
        (item) => item.management_number === CodesEnum.TRANSACTION_STATUS
      )
      return documentTypeInfo?.item ?? []
    })
    const documentTypeCodes = computed<CodesDetailItemsModel[]>(() => {
      const documentTypeInfo = loadCodesInformation()?.find(
        (item) => item.management_number === CodesEnum.DOCUMENT_TYPE
      )
      return documentTypeInfo?.item ?? []
    })
    // データをフォーマット
    data.forEach((item: TransactionHistoryItem) => {
      item.transaction.receive_user_name = item.transaction_status.receive_user_name
      item.transaction.revision_date = formatDate(item.transaction.revision_date ?? '')
      item.transaction.sent_date = formatDate(item.transaction.sent_date ?? '')
      item.transaction.receive_date = formatDate(item.transaction.receive_date ?? '')
      item.transaction.transaction_status =
        item.transaction_status.transaction_status || item.transaction.transaction_status
      transactionStatusCodes.value.forEach((items: CodesDetailItemsModel) => {
        if (items.value == item.transaction.transaction_status) {
          item.transaction.transaction_status_name = items.name
        }
      })
      documentTypeCodes.value.forEach((items: CodesDetailItemsModel) => {
        if (items.value == item.transaction.document_type) {
          item.transaction.document_type_name = items.name
        }
      })
    })
    commit(actionTypes.SET_REMIND_SEND_MAIL_DETAIL, data)
    commit(actionTypes.SET_REMIND_TOTAL_COUNT, result?.total_count ?? 0)
  },
  [actionTypes.SET_REMIND_PARAMS]: ({ commit }, params: Params): void => {
    commit(actionTypes.SET_REMIND_PARAMS, params)
  },
  [actionTypes.SET_REMIND_SEND_MAIL_CHOOSED_DETAIL]: ({ commit }, data: Array<any>): void => {
    commit(actionTypes.SET_REMIND_SEND_MAIL_CHOOSED_DETAIL, data)
  },
}

export default actions
